import { useTheme, useMediaQuery } from "@mui/material";

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface BreakpointState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLargeDesktop: boolean;
  currentBreakpoint: "xs" | "sm" | "md" | "lg" | "xl";
  screenWidth: number;
}

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export const useBreakpoint = (): BreakpointState => {
  const theme = useTheme();

  // 🔥 OPTIMIZACIÓN: Usar useMediaQuery para breakpoints consistentes
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // < 900px
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg")); // 900px - 1200px
  const isDesktop = useMediaQuery(theme.breakpoints.between("lg", "xl")); // 1200px - 1536px
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up("xl")); // >= 1536px

  // Determinar breakpoint actual
  const currentBreakpoint = (() => {
    if (isMobile) return "xs" as const;
    if (isTablet) return "md" as const;
    if (isDesktop) return "lg" as const;
    return "xl" as const;
  })();

  // Obtener ancho de pantalla (solo en cliente)
  const screenWidth = typeof window !== "undefined" ? window.innerWidth : 0;

  return {
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    currentBreakpoint,
    screenWidth,
  };
};

// ============================================================================
// HOOKS ESPECÍFICOS PARA CONVENIENCIA
// ============================================================================

export const useIsMobile = (): boolean => {
  return useBreakpoint().isMobile;
};

export const useIsTablet = (): boolean => {
  return useBreakpoint().isTablet;
};

export const useIsDesktop = (): boolean => {
  return useBreakpoint().isDesktop;
};

export const useIsLargeDesktop = (): boolean => {
  return useBreakpoint().isLargeDesktop;
};

// ============================================================================
// HOOK PARA DIMENSIONES ESPECÍFICAS
// ============================================================================

export const useResponsiveValue = <T>(values: {
  mobile?: T;
  tablet?: T;
  desktop?: T;
  largeDesktop?: T;
}): T | undefined => {
  const { isMobile, isTablet, isDesktop, isLargeDesktop } = useBreakpoint();

  if (isLargeDesktop && values.largeDesktop !== undefined) {
    return values.largeDesktop;
  }
  if (isDesktop && values.desktop !== undefined) {
    return values.desktop;
  }
  if (isTablet && values.tablet !== undefined) {
    return values.tablet;
  }
  if (isMobile && values.mobile !== undefined) {
    return values.mobile;
  }

  // Fallback al primer valor disponible
  return (
    values.largeDesktop || values.desktop || values.tablet || values.mobile
  );
};

// ============================================================================
// HOOK PARA CONFIGURACIÓN DE COMPONENTES
// ============================================================================

export const useResponsiveConfig = () => {
  const { isMobile, isTablet } = useBreakpoint();

  return {
    // Configuración de columnas para grids
    columns: isMobile ? 1 : isTablet ? 2 : 3,

    // Configuración de espaciado
    spacing: isMobile ? 1 : isTablet ? 2 : 3,

    // Configuración de padding
    padding: isMobile ? 1 : isTablet ? 2 : 3,

    // Configuración de tamaño de fuente
    fontSize: isMobile ? "body2" : isTablet ? "body1" : "h6",

    // Configuración de altura de componentes
    height: isMobile ? "auto" : "100dvh",

    // Configuración de ancho de componentes
    width: isMobile ? "100%" : isTablet ? "90%" : "80%",
  };
};
